import { Flex, Form, notification } from "antd";
import { type FC, useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
    getContragentsInfo,
    postContragentsCreateIllegal,
} from "tm-shared/api";
import { validationMessage } from "tm-shared/constants";

import BaseButton from "@/components/BaseButton";
import { BaseModal } from "@/components/BaseModal";
import { RHFTextField } from "@/components/RHF/RHFTextField";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
    setMarketCreatePageModal,
    setMarketCreatePageModalSelectionPayload,
} from "@/store/slices/ui";
import { NewIllegalEntityFormValues } from "@/types/form";
import { MarketCreateModal } from "@/types/modals";

interface AddIllegalEntityModalProps {
    onClose: () => void;
}

export const AddIllegalEntityModal: FC<AddIllegalEntityModalProps> = ({
    onClose,
}) => {
    const dispatch = useAppDispatch();
    const { newMarketFormValues } = useAppSelector(
        (s) => s.ui.marketCreatePage,
    );

    const emptyForm: NewIllegalEntityFormValues = useMemo(
        () => ({
            fullName: "",
            companyName: "",
        }),
        [],
    );

    const addIllegalEntityForm = useForm<NewIllegalEntityFormValues>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: emptyForm,
    });

    const {
        handleSubmit,
        formState: { isSubmitting },
    } = addIllegalEntityForm;

    const onSubmit = useCallback(
        async ({ fullName, companyName }: NewIllegalEntityFormValues) => {
            const region = newMarketFormValues?.region;

            try {
                if (region) {
                    const response = await postContragentsCreateIllegal({
                        fullName,
                        companyName,
                        mainRegionId: region,
                    });

                    const contragentId = response.result;

                    const { result: newContragent } = await getContragentsInfo({
                        contragent: contragentId,
                    });

                    dispatch(
                        setMarketCreatePageModalSelectionPayload({
                            fieldName: "legalEntity",
                            option: {
                                label: newContragent.fullName,
                                value: newContragent.id,
                            },
                            value: newContragent.id,
                        }),
                    );

                    onClose();
                }
            } catch (error) {
                notification.error({
                    message: "Не вдалося створити тіньового контрагента",
                });

                return;
            }
        },
        [dispatch, newMarketFormValues?.region, onClose],
    );

    const handleCreateIllegal = useCallback(() => {
        dispatch(
            setMarketCreatePageModal({
                type: MarketCreateModal.AddIllegalEntity,
            }),
        );
    }, [dispatch]);

    return (
        <BaseModal
            open
            onClose={() => !isSubmitting && onClose()}
            actions={null}
            title={"Додати тіньового контрагента"}
            maskClosable={false}
        >
            <Flex vertical gap={8}>
                <PrimaryText color="textDisabled">
                    Для додавання тіньового контрагента, заповніть форму
                </PrimaryText>

                <FormProvider {...addIllegalEntityForm}>
                    <Form
                        layout="vertical"
                        component="form"
                        onFinish={handleSubmit(onSubmit)}
                    >
                        <Flex vertical gap={8} style={{ marginBottom: 16 }}>
                            <RHFTextField
                                name={"companyName"}
                                label={"Назва компанії"}
                                rules={{
                                    required: validationMessage.required,
                                }}
                                disabled={isSubmitting}
                                size="large"
                            />

                            <RHFTextField
                                name={"fullName"}
                                label={"Юридична особа"}
                                rules={{
                                    required: validationMessage.required,
                                }}
                                disabled={isSubmitting}
                                size="large"
                            />
                        </Flex>

                        <Flex vertical gap={8}>
                            <BaseButton
                                size="large"
                                type="default"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Скасувати
                            </BaseButton>

                            <BaseButton
                                type="primary"
                                htmlType="submit"
                                loading={isSubmitting}
                                onClick={handleCreateIllegal}
                            >
                                Створити тіньового контрагента
                            </BaseButton>
                        </Flex>
                    </Form>
                </FormProvider>
            </Flex>
        </BaseModal>
    );
};
